import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { theme } from './styles/theme';

// Layout Components
import TopNav from './components/layout/TopNav';
import Footer from './components/layout/Footer';
import CartDrawer from './components/cart/CartDrawer';
import ScrollToTop from './components/layout/ScrollToTop';

// Route Pages
import HomePage from './routes/HomePage';
import CategoryPage from './routes/CategoryPage';
import ProductDetailPage from './routes/ProductDetailPage';
import CartPage from './routes/CartPage';
import CheckoutPage from './routes/CheckoutPage';
import PaymentPage from './routes/PaymentPage';
import PaymentSuccessPage from './routes/PaymentSuccessPage';
import CustomerDashboard from './routes/CustomerDashboard';
import AdminDashboard, { preloadAdminDashboard } from './routes/LazyAdminDashboard';
import StyleGuidePage from './routes/StyleGuidePage';
import PrivacyPolicyPage from './routes/PrivacyPolicyPage';
import TermsAndConditionsPage from './routes/TermsAndConditionsPage';
import AboutPage from './routes/AboutPage';
import ContactPage from './routes/ContactPage';
import WholesalePage from './routes/WholesalePage';
import ShippingPolicyPage from './routes/ShippingPolicyPage';
import ReturnsPage from './routes/ReturnsPage';
import ProductDisclaimerPage from './routes/ProductDisclaimerPage';

// Protected Route Component
const ProtectedRoute = ({ children, requireAuth = true, requireAdmin = false }) => {
  const { isAuthenticated, isAdmin, loading, userProfile } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.background,
      }}>
        <p style={{ color: theme.colors.textSecondary }}>Loading...</p>
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    if (process.env.NODE_ENV === 'development') {
      console.log('ProtectedRoute: User not authenticated, redirecting to /');
    }
    return <Navigate to="/" replace />;
  }

  if (requireAdmin && !isAdmin) {
    if (process.env.NODE_ENV === 'development') {
      console.log('ProtectedRoute: Admin access denied', {
        isAdmin,
        userRole: userProfile?.role,
        userEmail: userProfile?.email,
      });
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Main App Layout
const AppLayout = ({ children }) => {
  const { isAdmin } = useAuth();

  // Once an admin is signed in, fetch the admin panel's code in the
  // background so it opens instantly. Nobody else ever downloads it.
  useEffect(() => {
    if (!isAdmin) return undefined;
    if ('requestIdleCallback' in window) {
      const handle = window.requestIdleCallback(preloadAdminDashboard, { timeout: 5000 });
      return () => window.cancelIdleCallback(handle);
    }
    const handle = window.setTimeout(preloadAdminDashboard, 2000);
    return () => window.clearTimeout(handle);
  }, [isAdmin]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
    }}>
      <TopNav />
      <main style={{ flex: 1 }}>
        {children}
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
};

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <AuthProvider>
        <CartProvider>
          <AppLayout>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/style-guide" element={<StyleGuidePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsAndConditionsPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/wholesale" element={<WholesalePage />} />
              <Route path="/shipping" element={<ShippingPolicyPage />} />
              <Route path="/returns" element={<ReturnsPage />} />
              <Route path="/product-disclaimer" element={<ProductDisclaimerPage />} />
              <Route path="/products" element={<CategoryPage />} />
              <Route path="/products/:categorySlug" element={<CategoryPage />} />
              <Route path="/product/:productId" element={<ProductDetailPage />} />
              {/* API Featured Asset Routes */}
              <Route path="/category/:categoryId" element={<CategoryPage />} />
              <Route path="/cart" element={<CartPage />} />
              
              {/* Payment Routes (public) */}
              <Route path="/pay" element={<PaymentPage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />

              {/* Protected Routes */}
              <Route
                path="/checkout"
                element={
                  <ProtectedRoute requireAuth>
                    <CheckoutPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute requireAuth>
                    <CustomerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireAuth requireAdmin>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />

              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AppLayout>
        </CartProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;

